/* Blessed and Redeemed Boutique theme script.
   Builds the shop filters, home product rows, product pages and cart drawer from the catalog feed
   (/collections/<handle>?view=catalog). Categories and seasons come from product titles until
   products are tagged in Shopify; tags of the same name take priority when present. */
(() => {
  const S = window.BR_SETTINGS || {};
  const OWN_VENDOR = (S.ownVendor || "Blessed and Redeemed Boutique").trim();
  const HIDDEN_PHOTOS = (S.hiddenPhotos || []).map(s => String(s).trim()).filter(Boolean);
  const NEW_COUNT = Number(S.newCount) || 16;

  const $ = id => document.getElementById(id);
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
  const money = n => "$" + n.toFixed(2);
  // Some suppliers send colors in all caps ("OATMEAL"); show them as "Oatmeal".
  const niceColor = c => c === c.toUpperCase() ? c.toLowerCase().replace(/\b\w/g, m => m.toUpperCase()) : c;
  const visible = src => src && !HIDDEN_PHOTOS.some(h => src.includes(h));
  const sized = (url, w) => url ? url.replace(/([?&])width=\d+/, `$1width=${w}`) : url;

  // ---------- catalog: turn Shopify products into the shape the pages use ----------
  const TYPES = [
    { id: "tops", label: "Tops & Blouses" },
    { id: "sweaters", label: "Sweaters & Cardigans" },
    { id: "graphic", label: "Sweatshirts & Tees" },
    { id: "dresses", label: "Dresses" },
    { id: "bottoms", label: "Pants, Jeans & Skirts" },
    { id: "sets", label: "Matching Sets" },
    { id: "shoes", label: "Shoes" },
    { id: "accessories", label: "Bags" },
    { id: "kids", label: "Kids" }
  ];
  const SEASONS = [
    { id: "fall", label: "Fall & Winter" },
    { id: "christmas", label: "Christmas" },
    { id: "springsummer", label: "Spring & Summer" },
    { id: "faith", label: "Faith Collection" },
    { id: "gingham", label: "Gingham & Polka Dots" }
  ];
  const PRICES = [
    { id: "u30", label: "Under $30", test: p => p < 30 },
    { id: "30-50", label: "$30 – $50", test: p => p >= 30 && p <= 50 },
    { id: "o50", label: "Over $50", test: p => p > 50 }
  ];
  const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "1X", "2X", "3X", "4X"];
  const SIZE_MAP = { XS: "XS", S: "S", M: "M", L: "L", XL: "XL", XXL: "2X", "2XL": "2X", "2X": "2X", "1X": "1X", "1XL": "1X", XXXL: "3X", "3XL": "3X", "3X": "3X", "4XL": "4X" };
  const typeLabel = id => (TYPES.find(t => t.id === id) || TYPES[0]).label;

  // Liquid's strip_html keeps line breaks and HTML entities (&amp;); decode and flatten so the parsers see plain text.
  const decodeEntities = s => { const t = document.createElement("textarea"); t.innerHTML = String(s || ""); return t.value; };
  const flatText = s => decodeEntities(s).replace(/\s+/g, " ").trim();
  const fixText = s => String(s || "").replace(/â€œ/g, "“").replace(/â€\u009d/g, "”").replace(/â€™/g, "’").replace(/â€/g, "”").replace(/Â®/g, "®").replace(/Â/g, "").trim();
  const tagSet = tags => new Set((tags || []).map(t => t.toLowerCase()));

  function cleanTitle(raw, own) {
    let title = fixText(String(raw).split(" | ")[0]), subtitle = "", plus = false;
    const q = own && title.match(/^“([^”]+)”\s*(.*)$/);
    if (q) { title = q[1].replace(/\b\w/g, c => c.toUpperCase()); subtitle = q[2]; }
    else if (own && title.includes("-")) { const k = title.indexOf("-"); subtitle = title.slice(k + 1).trim(); title = title.slice(0, k).trim().replace(/\b\w/g, c => c.toUpperCase()); }
    if (/^Full Size /.test(title)) { title = title.replace(/^Full Size /, ""); plus = true; }
    if (/ Plus Size$/.test(title)) { title = title.replace(/ Plus Size$/, ""); plus = true; }
    return { title: title.replace(/\s{2,}/g, " "), subtitle, plus };
  }
  function classify(rawTitle, own, tags) {
    const fromTag = TYPES.find(t => tags.has(t.id) || tags.has(t.label.toLowerCase()));
    if (fromTag) return fromTag.id;
    const t = rawTitle.toLowerCase();
    if (/toddler|kids|youth/.test(t)) return "kids";
    if (/\bset\b|2pcs|two piece/.test(t)) return "sets";
    if (/boots|loafers|flats|heels|sneaker|shoes/.test(t)) return "shoes";
    if (/\bbag\b|purse|tote|wallet/.test(t)) return "accessories";
    if (/dress/.test(t)) return "dresses";
    if (/skirt|pants|jeans|shorts|denim/.test(t)) return "bottoms";
    if (/cardigan|sweater/.test(t) && !/sweatshirt/.test(t)) return "sweaters";
    if (/sweatshirt|t-shirt|tee\b|hoodie|crewneck/.test(t) || own) return "graphic";
    return "tops";
  }
  function seasonsOf(rawTitle, type, own, tags) {
    const t = rawTitle.toLowerCase(), s = [];
    SEASONS.forEach(x => { if (tags.has(x.id) || tags.has(x.label.toLowerCase())) s.push(x.id); });
    if (/christmas|holiday|santa|snow/.test(t)) s.push("christmas");
    if (own || /jesus|pray|faith|blessed|grace/.test(t)) s.push("faith");
    if (/gingham|plaid|polka|dot/.test(t)) s.push("gingham");
    if (["sweaters", "graphic"].includes(type) || /long sleeve|corduroy|boots|turtleneck|knit|fuzzy|chenille|leopard|cardigan|quarter-zip|hooded/.test(t)) s.push("fall");
    if (/shorts|sleeveless|chiffon|linen|mini dress|puff sleeve/.test(t) && !/long sleeve|faux leather|corduroy/.test(t)) s.push("springsummer");
    return [...new Set(s)];
  }

  function normalize(r) {
    const own = r.vendor === OWN_VENDOR;
    const tags = tagSet(r.tags);
    const { title, subtitle, plus: plusTitle } = cleanTitle(r.title, own);
    const names = (r.options || []).map(n => String(n).toLowerCase());
    const ci = names.findIndex(n => n === "color" || n === "colour");
    const si = names.findIndex(n => n === "size");
    const variants = r.variants.map(v => [ci >= 0 ? v.o[ci] : "", si >= 0 ? v.o[si] : "", v.price / 100, v.ok ? 1 : 0, v.id, v.img]);
    const uniq = a => [...new Set(a.filter(Boolean))];
    const colors = uniq(variants.map(v => v[0]));
    const sizeLabels = uniq(variants.map(v => v[1]));
    const sizes = uniq(sizeLabels.map(v => SIZE_MAP[v.toUpperCase()]));
    const colorImgs = {};
    colors.forEach(c => { const v = variants.find(x => x[0] === c && visible(x[5])); if (v) colorImgs[c] = v[5]; });
    let imgs = (r.images || []).filter(visible).slice(0, 2);
    if (!imgs.length) imgs = Object.values(colorImgs).slice(0, 1);
    const prices = variants.map(v => v[2]);
    const shipTag = (r.tags || []).find(t => /^ship from/i.test(t));
    return {
      id: r.handle, handle: r.handle, title, subtitle, own,
      type: classify(r.title, own, tags),
      seasons: seasonsOf(r.title, classify(r.title, own, tags), own, tags),
      price: Math.min(...prices), maxPrice: Math.max(...prices),
      compare: r.compare ? r.compare / 100 : null,
      sizes, sizeLabels, colors, colorImgs,
      plus: plusTitle || sizes.some(s => ["1X", "2X", "3X", "4X"].includes(s)),
      created: r.created, variants,
      soldOut: variants.every(v => !v[3]),
      ships: shipTag ? (/usa/i.test(shipTag) ? "usa" : "overseas") : (own ? "usa" : ""),
      desc: fixText(flatText(r.desc)), imgs
    };
  }

  // Trendsi lists some styles once per color under the same title; show each group as one product.
  function combineColorListings(list) {
    const groups = new Map();
    list.forEach(p => { const k = p.own ? p.id : p.title.toLowerCase(); groups.set(k, [...(groups.get(k) || []), p]); });
    return [...groups.values()].map(g => {
      if (g.length === 1) return { ...g[0], listings: [g[0].handle] };
      const pick = f => [...new Set(g.flatMap(f))];
      return {
        ...g[0],
        colors: pick(p => p.colors),
        colorImgs: Object.assign({}, ...g.map(p => p.colorImgs)),
        variants: g.flatMap(p => p.variants),
        sizeLabels: pick(p => p.sizeLabels), sizes: pick(p => p.sizes), seasons: pick(p => p.seasons),
        price: Math.min(...g.map(p => p.price)), maxPrice: Math.max(...g.map(p => p.maxPrice)),
        plus: g.some(p => p.plus), soldOut: g.every(p => p.soldOut),
        created: g.map(p => p.created).sort().pop(),
        listings: g.map(p => p.handle)
      };
    });
  }

  let catalogPromise = null;
  function loadCatalog(handle) {
    if (!catalogPromise) {
      catalogPromise = fetch(`/collections/${handle || "all"}?view=catalog`, { credentials: "same-origin" })
        .then(r => { if (!r.ok) throw new Error("catalog " + r.status); return r.json(); })
        .then(raw => {
          const products = combineColorListings(raw.map(normalize));
          const byDate = [...products].sort((a, b) => b.created.localeCompare(a.created));
          const newIds = new Set(byDate.slice(0, NEW_COUNT).map(p => p.id));
          products.forEach(p => { p.isNew = newIds.has(p.id); p.sale = p.compare && p.compare > p.price ? p.compare : null; });
          return { products, byDate, featured: byDate.filter(p => !p.soldOut) };
        });
    }
    return catalogPromise;
  }

  // ---------- product cards ----------
  function priceHtml(p) {
    if (p.sale) return `${money(p.price)}<s>${money(p.sale)}</s>`;
    return p.maxPrice > p.price ? `From ${money(p.price)}` : money(p.price);
  }
  function sizeSummary(p) {
    const l = p.sizeLabels;
    if (!l.length || /one ?size/i.test(l[0])) return "One size";
    return l.length > 2 ? `${l[0]}–${l[l.length - 1]}` : l.join(", ");
  }
  function card(p) {
    const tag = p.soldOut ? `<span class="tag">Sold out</span>` : p.sale ? `<span class="tag sale">Sale</span>` : p.own ? `<span class="tag">B&amp;R Original</span>` : p.isNew ? `<span class="tag">New</span>` : "";
    const alt = p.imgs[1] ? `<img class="alt" src="${esc(p.imgs[1])}" alt="" loading="lazy">` : "";
    const colors = p.colors.length > 1 ? ` · ${p.colors.length} colors` : "";
    return `<a class="card" href="/products/${esc(p.handle)}"><div class="card-img">${tag}${p.imgs[0] ? `<img src="${esc(p.imgs[0])}" alt="${esc(p.title)}" loading="lazy">` : ""}${alt}</div><div class="card-name">${esc(p.title)}</div>${p.subtitle ? `<div class="card-sub">${esc(p.subtitle)}</div>` : ""}<div class="price">${priceHtml(p)}</div><div class="card-sizes">${esc(sizeSummary(p))}${colors}</div></a>`;
  }

  // ---------- home ----------
  function initHome() {
    const rows = document.querySelectorAll("[data-row]");
    const cats = document.querySelector("[data-categories]");
    const orig = document.querySelector("[data-originals-photos]");
    if (!rows.length && !cats && !orig) return;
    loadCatalog("all").then(({ featured }) => {
      rows.forEach(el => {
        const src = el.dataset.row, n = Number(el.dataset.count) || 4;
        const list = src === "new" ? featured.filter(p => !p.own) : src === "own" ? featured.filter(p => p.own) : featured.filter(p => p.seasons.includes(src) && !p.own);
        el.innerHTML = list.slice(0, n).map(card).join("") || `<p class="loading">New pieces coming soon.</p>`;
      });
      if (cats) cats.querySelectorAll("[data-cat]").forEach(a => {
        const p = featured.find(x => x.type === a.dataset.cat && !x.own) || featured.find(x => x.type === a.dataset.cat);
        if (p && p.imgs[0]) a.querySelector(".cat-art").innerHTML = `<img src="${esc(p.imgs[0])}" alt="" loading="lazy">`;
      });
      if (orig) orig.innerHTML = featured.filter(p => p.own && p.type !== "kids").slice(0, 3).map(p => `<img src="${esc(p.imgs[0])}" alt="${esc(p.title + " " + p.subtitle)}" loading="lazy">`).join("");
    }).catch(() => rows.forEach(el => { el.innerHTML = `<p class="loading"><a href="/collections/all">Shop all products</a></p>`; }));
  }

  // ---------- shop with filters ----------
  function initShop() {
    const root = document.querySelector("[data-shop]");
    if (!root) return;
    const filters = $("filters");
    const state = { types: new Set(), seasons: new Set(), sizes: new Set(), prices: new Set(), toggles: new Set(), sort: "featured" };
    const TOGGLES_ALL = [
      { id: "new", label: "New arrivals", test: p => p.isNew },
      { id: "own", label: "B&R Originals", test: p => p.own },
      { id: "plus", label: "Plus sizes", test: p => p.plus },
      { id: "sale", label: "On sale", test: p => !!p.sale }
    ];
    let PRODUCTS = [], TOGGLES = [], SIZES = [];

    // Filters live in the address (?type=dresses&season=fall&show=new) so links and the back button work.
    const params = new URLSearchParams(location.search);
    const fromParam = (key, set) => (params.get(key) || "").split(",").filter(Boolean).forEach(v => set.add(v));
    fromParam("type", state.types); fromParam("season", state.seasons); fromParam("size", state.sizes); fromParam("price", state.prices); fromParam("show", state.toggles);
    if (params.get("sort")) state.sort = params.get("sort");
    function syncUrl() {
      const q = new URLSearchParams();
      const put = (k, set) => { if (set.size) q.set(k, [...set].join(",")); };
      put("type", state.types); put("season", state.seasons); put("size", state.sizes); put("price", state.prices); put("show", state.toggles);
      if (state.sort !== "featured") q.set("sort", state.sort);
      const s = q.toString();
      history.replaceState(null, "", location.pathname + (s ? "?" + s : ""));
      document.querySelectorAll("[data-nav]").forEach(a => a.removeAttribute("aria-current"));
      const onlyNew = state.toggles.size === 1 && state.toggles.has("new") && !state.types.size && !state.seasons.size;
      const navA = document.querySelector(`[data-nav="${onlyNew ? "new" : "shop"}"]`);
      if (navA) navA.setAttribute("aria-current", "page");
    }
    const matches = (p, skip) => {
      if (skip !== "types" && state.types.size && !state.types.has(p.type)) return false;
      if (skip !== "seasons" && state.seasons.size && !p.seasons.some(s => state.seasons.has(s))) return false;
      if (skip !== "sizes" && state.sizes.size && !p.sizes.some(s => state.sizes.has(s))) return false;
      if (skip !== "prices" && state.prices.size && !PRICES.some(r => state.prices.has(r.id) && r.test(p.price))) return false;
      for (const t of TOGGLES) if (state.toggles.has(t.id) && !t.test(p)) return false;
      return true;
    };
    function optList(container, items, key) {
      container.innerHTML = items.map(it => {
        const n = PRODUCTS.filter(p => matches(p, key) && (key === "types" ? p.type === it.id : key === "seasons" ? p.seasons.includes(it.id) : it.test(p.price))).length;
        const on = state[key].has(it.id);
        return `<label class="opt${n === 0 && !on ? " dim" : ""}"><input type="checkbox" id="f-${key}-${it.id}" data-key="${key}" value="${it.id}"${on ? " checked" : ""}>${esc(it.label)}<span class="count">${n}</span></label>`;
      }).join("");
    }
    function render() {
      optList($("fType"), TYPES, "types");
      optList($("fSeason"), SEASONS, "seasons");
      optList($("fPrice"), PRICES, "prices");
      $("fSize").innerHTML = SIZES.map(s => `<button class="size-chip" data-size="${s}" aria-pressed="${state.sizes.has(s)}">${s}</button>`).join("");
      $("fToggles").innerHTML = TOGGLES.map(t => `<button class="size-chip" data-toggle="${t.id}" aria-pressed="${state.toggles.has(t.id)}">${esc(t.label)}</button>`).join("");
      $("sort").value = state.sort;

      const list = PRODUCTS.filter(p => matches(p));
      if (state.sort === "low") list.sort((a, b) => a.price - b.price);
      else if (state.sort === "high") list.sort((a, b) => b.price - a.price);
      else list.sort((a, b) => b.created.localeCompare(a.created));
      list.sort((a, b) => a.soldOut - b.soldOut); // sold-out items sink to the end
      $("shopGrid").innerHTML = list.length ? list.map(card).join("") :
        `<div class="empty" style="grid-column:1/-1"><div class="script">Oh no!</div><p>Nothing matches all of those filters. Try removing one.</p><button class="btn ghost" data-clear>Clear all filters</button></div>`;
      $("resultCount").textContent = list.length === 1 ? "1 item" : `${list.length} items`;

      const chips = [];
      state.toggles.forEach(t => { const x = TOGGLES.find(y => y.id === t); if (x) chips.push(["toggles", t, x.label]); });
      state.types.forEach(t => chips.push(["types", t, typeLabel(t)]));
      state.seasons.forEach(t => { const x = SEASONS.find(y => y.id === t); if (x) chips.push(["seasons", t, x.label]); });
      state.sizes.forEach(t => chips.push(["sizes", t, "Size " + t]));
      state.prices.forEach(t => { const x = PRICES.find(y => y.id === t); if (x) chips.push(["prices", t, x.label]); });
      $("chips").innerHTML = chips.map(([k, v, l]) => `<button class="chip" data-chip="${k}" data-val="${esc(v)}">${esc(l)} <span aria-hidden="true">×</span><span class="sr">Remove</span></button>`).join("") + (chips.length > 1 ? `<button class="chip clear" data-clear>Clear all</button>` : "");

      if (root.dataset.collection === "all") {
        let title = "Shop All", sub = "Everything in the boutique, all in one place.";
        const quiet = !state.types.size && !state.seasons.size;
        if (state.toggles.size === 1 && quiet) {
          [title, sub] = { new: ["New Arrivals", "Fresh pieces, added every week."], own: ["B&R Originals", "Our own designs, printed just for you."], plus: ["Plus Sizes", "Styles that come in 1X, 2X, 3X and beyond."], sale: ["Sale", "Favorites at a little-bit-sweeter price."] }[[...state.toggles][0]] || [title, sub];
        } else if (state.seasons.size === 1 && !state.types.size && !state.toggles.size) {
          const s = SEASONS.find(x => state.seasons.has(x.id));
          if (s) { title = s.label; sub = s.id === "faith" ? "Tees and sweatshirts that carry a little encouragement." : `Our ${s.label.toLowerCase()} favorites.`; }
        } else if (state.types.size === 1 && !state.seasons.size && !state.toggles.size) {
          title = typeLabel([...state.types][0]); sub = `Shop all ${title.toLowerCase()}.`;
        }
        $("shopTitle").textContent = title; $("shopSub").textContent = sub;
        document.title = `${title} | Blessed and Redeemed Boutique`;
      }
      syncUrl();
    }
    const resetFilters = () => { state.types.clear(); state.seasons.clear(); state.sizes.clear(); state.prices.clear(); state.toggles.clear(); };

    filters.addEventListener("change", e => {
      const i = e.target; if (!i.dataset.key) return;
      state[i.dataset.key][i.checked ? "add" : "delete"](i.value);
      render();
      const again = $(i.id); if (again) again.focus();
    });
    filters.addEventListener("click", e => {
      const s = e.target.closest("[data-size]"), t = e.target.closest("[data-toggle]");
      if (s) { const v = s.dataset.size; state.sizes.has(v) ? state.sizes.delete(v) : state.sizes.add(v); render(); $("fSize").querySelector(`[data-size="${v}"]`).focus(); }
      if (t) { const v = t.dataset.toggle; state.toggles.has(v) ? state.toggles.delete(v) : state.toggles.add(v); render(); $("fToggles").querySelector(`[data-toggle="${v}"]`).focus(); }
    });
    $("sort").addEventListener("change", e => { state.sort = e.target.value; render(); });
    root.addEventListener("click", e => {
      const c = e.target.closest("[data-chip]");
      if (c) { state[c.dataset.chip].delete(c.dataset.val); render(); }
      if (e.target.closest("[data-clear]")) { resetFilters(); render(); }
    });
    $("filterBtn").addEventListener("click", () => { filters.classList.add("open"); $("scrim").classList.add("show"); });
    $("filtersClose").addEventListener("click", closeAll);

    loadCatalog(root.dataset.collection).then(({ products }) => {
      PRODUCTS = products;
      TOGGLES = TOGGLES_ALL.filter(t => PRODUCTS.some(t.test));
      SIZES = SIZE_ORDER.filter(s => PRODUCTS.some(p => p.sizes.includes(s)));
      render();
    }).catch(() => { /* keep the server-rendered grid */ });
  }

  // ---------- product page ----------
  // B&R Originals come from Printify with long blank-garment descriptions (cm and inch tables, care tables).
  // Each blank gets short Features / Material / Care lines and an inch size chart, matching the Trendsi items.
  const SPECS = {
    "18000": {
      lines: [["Features", "Soft heavy-blend fleece with air jet yarn for less pilling, double-needle stitching and ribbed cuffs and waistband with a little stretch. Classic fit."], ["Material composition", "50% cotton, 50% polyester"], ["Care instructions", "Machine wash warm with like colors. Tumble dry medium."]],
      head: ["Size", "Length", "Chest", "Sleeve Length"],
      rows: [["S", 27.2, 40.2, 33.5], ["M", 28, 44.1, 34.6], ["L", 29.1, 48, 35.4], ["XL", 29.9, 52, 36.6], ["2XL", 31.1, 55.9, 37.4], ["3XL", 31.9, 59.8, 38.6], ["4XL", 33, 64, 39.5], ["5XL", 34, 68, 40.5]]
    },
    "c1717": {
      lines: [["Features", "Pre-shrunk, soft-washed and garment-dyed for a vintage look and minimal shrinkage. Twill-taped neck and shoulders, double-needle stitched sleeves and hem, and a 1\" ribbed collar."], ["Material composition", "100% US-grown ring-spun cotton"], ["Care instructions", "Tumble dry low or air dry."]],
      head: ["Size", "Length", "Chest", "Sleeve Length"],
      rows: [["S", 26.6, 35, 8], ["M", 28, 39, 8.3], ["L", 29.4, 42.5, 13.3], ["XL", 30.7, 46, 9.5], ["2XL", 31.6, 50, 9.8], ["3XL", 32.5, 54, 9.8], ["4XL", 33.5, 58, 10]]
    },
    "5000b": {
      lines: [["Features", "Soft, comfortable heavyweight cotton with durable double-stitching at the neckline and sleeves. Pre-shrunk for a consistent fit after washing."], ["Care instructions", "Machine wash cold with like colors. Tumble dry low."]],
      head: ["Size", "Length", "Chest", "Sleeve Length"],
      rows: [["XS", 20.5, 32, 13.3], ["S", 22, 34, 14.3], ["M", 23.5, 36, 15.2], ["L", 25, 38, 16.3], ["XL", 26.5, 40, 17.2]]
    },
    "3321": {
      lines: [["Features", "A soft, durable crew neck tee made for playtime, with a topstitched ribbed collar, double-needle sleeves and hem, and a tear-away label."], ["Material composition", "100% combed ring-spun cotton (Heather is 90% cotton, 10% polyester)"], ["Care instructions", "Machine wash. Tumble dry low."]],
      head: ["Size", "Length", "Chest", "Sleeve Length"],
      rows: [["2T", 15.5, 26, 4.8], ["3T", 16.5, 28, 5], ["4T", 17.5, 30, 5.2], ["5T", 18.5, 32, 5.5]]
    }
  };
  const specFor = p => p.own ? Object.keys(SPECS).map(k => p.handle.includes(k) ? SPECS[k] : null).find(Boolean) : null;
  const tableHtml = (head, rows) => `<div class="table-scroll"><table class="size-table"><thead><tr>${head.map(h => `<th>${esc(h)}</th>`).join("")}</tr></thead><tbody>${rows.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join("")}</tr>`).join("")}</tbody></table></div><p style="font-size:13px;color:var(--ink-muted)">Measurements in inches.</p>`;

  // Printify size guides list each measurement as a row across the sizes, in cm and then inches:
  // "Size guide S M L ... A) Length (inches) 27.2 28 ... B) Width (inches) ...". Turn the inch rows into
  // one row per size (Size, Length, Chest, Sleeve Length), matching the Trendsi charts.
  function printifyTable(p) {
    const d = p.desc, at = d.search(/Size guide/i);
    if (at < 0) return "";
    const tok = d.slice(at).replace(/^Size guide\s*/i, "").split(" ");
    const sizes = [];
    for (const t of tok) { if (/^[A-Z0-9]{1,4}$/.test(t) && !/^[A-Z]\)$/.test(t)) sizes.push(t); else break; }
    if (!sizes.length) return "";
    const cols = [];
    const re = new RegExp(`(?:[A-Z]\\) )?([A-Za-z ]+?) \\(inches\\)((?: [\\d.]+){${sizes.length}})`, "g");
    let m;
    while ((m = re.exec(d))) {
      const name = m[1].trim();
      if (/half chest/i.test(name)) continue;
      cols.push([/^width$/i.test(name) ? "Chest" : name, m[2].trim().split(" ")]);
    }
    if (!cols.length) return "";
    const rows = sizes.map((s, i) => [s, ...cols.map(c => c[1][i])]).filter(r => !p.sizeLabels.length || p.sizeLabels.includes(r[0]));
    return rows.length ? tableHtml(["Size", ...cols.map(c => c[0])], rows) : "";
  }
  // Printify's text before the size guide, with its "Features:" / "Fabrication:" labels, plus the wash and dry lines.
  function printifyLines(p) {
    const d = p.desc, at = d.search(/Size guide/i);
    const intro = (at < 0 ? d : d.slice(0, at)).replace(/This product is made on demand\. No minimums\./i, "").trim();
    const parts = intro.split(/(?=Features:|Fabrication:)/).map(s => s.trim()).filter(Boolean);
    const care = [(d.match(/\bWash ([^.]*\.)/) || [])[1], (d.match(/\b(?:Tumble )?Dry ([^.]*\.)/) || [])[1]].filter(Boolean).join(" ");
    return parts.map(s => { const k = s.match(/^(Features|Fabrication):\s*(.*)$/); return k ? `<div><strong>${k[1] === "Fabrication" ? "Material composition" : k[1]}:</strong> ${esc(k[2])}</div>` : `<div>${esc(s)}</div>`; }).join("")
      + (care ? `<div><strong>Care instructions:</strong> ${esc(care)}</div>` : "");
  }
  // Some Trendsi brands write "measurements: S: Bust 33-35 in, Waist 25-27 in M: Bust ...".
  function inlineMeasurements(p) {
    const m = p.desc.match(/measurements:\s*(.*)$/i);
    if (!m || !p.sizeLabels.length) return null;
    const labels = [...p.sizeLabels].sort((a, b) => b.length - a.length).map(l => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const re = new RegExp(`(?:^|\\s|(?<=in))(${labels.join("|")})\\s*:`, "g");
    const hits = [...m[1].matchAll(re)];
    if (hits.length < 2) return null;
    const head = [], rows = [];
    hits.forEach((h, i) => {
      const chunk = m[1].slice(h.index + h[0].length, i + 1 < hits.length ? hits[i + 1].index : undefined);
      const row = { size: h[1] };
      chunk.split(",").forEach(part => {
        const pm = part.trim().match(/^([A-Za-z ]+?)\s+([\d.\-\/]+)\s*(?:in)?$/i);
        if (pm) { const name = pm[1].trim().replace(/^\w/, c => c.toUpperCase()); if (!head.includes(name)) head.push(name); row[name] = pm[2]; }
      });
      rows.push(row);
    });
    if (!head.length) return null;
    return { before: p.desc.slice(0, m.index), table: tableHtml(["Size", ...head], rows.map(r => [r.size, ...head.map(h => r[h] || "")])) };
  }

  // Supplier descriptions arrive as one run-on line; break them at their labels and turn the size table into a real table.
  function formatDesc(p) {
    const spec = specFor(p);
    if (p.own) {
      const rows = spec ? spec.rows.filter(r => p.sizeLabels.includes(r[0])) : [];
      return {
        html: spec ? spec.lines.map(([k, v]) => `<div><strong>${esc(k)}:</strong> ${esc(v)}</div>`).join("") : printifyLines(p),
        table: rows.length ? tableHtml(spec.head, rows) : printifyTable(p)
      };
    }
    let d = p.desc, table = "";
    const inline = inlineMeasurements(p);
    if (inline) { d = inline.before; table = inline.table; }
    const m = table ? null : d.match(/(?:Product )?Measurements[^&]*&\s*Size Conversion\s+(.*)$/i);
    if (m) {
      const rest = d.slice(m.index);
      d = d.slice(0, m.index);
      const tok = m[1].replace(/\bOne Size\b/gi, "One_Size").trim().split(/\s+/);
      // A chart may say "2XL" where the listing says "XXL"; treat sizes that mean the same thing as a match.
      const same = (a, b) => SIZE_MAP[a.toUpperCase()] && SIZE_MAP[a.toUpperCase()] === SIZE_MAP[b.toUpperCase()];
      const isSize = t => { const v = t.replace(/_/g, " ").replace(/\(.*$/, ""); return p.sizeLabels.some(l => l === v || same(l, v)); };
      const first = tok.findIndex((t, i) => i > 0 && isSize(t));
      const second = first > 0 ? tok.findIndex((t, i) => i > first && isSize(t)) : -1;
      const cols = second > first ? second - first : tok.length - first;
      const head = tok.slice(0, first);
      // Merge multi-word headers ("Top Length", "Waist Stretch Amount") until they line up with the data columns.
      for (let i = head.length - 1; head.length > cols && i > 1;) {
        if (/^(Length|Width|Circumference|Opening|Height|Amount|Stretch Amount)$/i.test(head[i])) { head.splice(i - 1, 2, head[i - 1] + " " + head[i]); i--; }
        else i--;
      }
      if (first > 0 && head.length === cols) {
        const rows = [];
        for (let i = first; i + cols <= tok.length; i += cols) rows.push(tok.slice(i, i + cols).map(c => c.replace(/_/g, " ")));
        if (rows.length && rows.every(r => isSize(r[0]) && r.slice(1).every(c => /^[\d.\-\/]+$/.test(c)))) table = tableHtml(head, rows);
      }
      if (!table) d += " " + rest;
    }
    const parts = d.split(/(?=Features:|Stretch:|Body:|Material composition:|Material:|Care instructions:|Imported\b)/).map(s => s.trim()).filter(Boolean);
    const html = parts.map(s => { const k = s.match(/^([A-Z][a-z ]+):\s*(.*)$/); return k ? `<div><strong>${esc(k[1])}:</strong> ${esc(k[2])}</div>` : `<div>${esc(s)}</div>`; }).join("");
    return { html, table };
  }

  function initProduct() {
    const pdp = $("pdp");
    if (!pdp || !pdp.dataset.handle) return;
    const handle = pdp.dataset.handle;
    let p = null;
    const st = { size: null, color: null, qty: 1 };

    const inStock = (color, size) => p.variants.some(v => (color == null || v[0] === color) && (size == null || v[1] === size) && v[3]);
    const current = () => {
      const c = p.colors.length ? st.color : "", s = p.sizeLabels.length ? st.size : "";
      if (c == null || s == null) return null;
      return p.variants.find(v => v[0] === c && v[1] === s) || null;
    };
    function refresh() {
      pdp.querySelectorAll("[data-pcolor]").forEach(b => { const out = !inStock(b.dataset.pcolor, null); b.classList.toggle("out", out); b.title = out ? "Sold out" : ""; });
      pdp.querySelectorAll("[data-psize]").forEach(b => { const out = !inStock(p.colors.length ? st.color : null, b.dataset.psize); b.classList.toggle("out", out); b.title = out ? "Sold out" : ""; });
      const v = current();
      $("pdpPrice").innerHTML = v ? money(v[2]) : priceHtml(p);
      const anyLeft = p.variants.some(x => x[3]);
      const btn = $("addBtn");
      btn.disabled = !anyLeft || (v && !v[3]);
      btn.textContent = !anyLeft ? "Sold out" : v && !v[3] ? "Sold out in this size" : "Add to cart";
    }
    function render(related) {
      const oneSize = p.sizeLabels.length <= 1;
      st.size = oneSize ? (p.sizeLabels[0] || "") : null;
      st.color = p.colors.length === 1 ? p.colors[0] : (p.colors.length ? null : "");
      $("crumbs").innerHTML = `<a href="/">Home</a> / <a href="/collections/all?type=${p.type}">${esc(typeLabel(p.type))}</a> / ${esc(p.title)}`;
      const desc = formatDesc(p);
      const thumbs = [...new Set([...p.imgs, ...p.colors.map(c => p.colorImgs[c]).filter(Boolean)])].slice(0, 8);
      const ship = p.own ? "Printed just for you when you order, then shipped. You'll get tracking by email."
        : p.ships === "usa" ? "Ships from our partner warehouse in the USA. You'll get tracking by email once it's on its way."
        : "Ships from our overseas partner, so it can take a little longer to arrive. You'll get tracking by email once it's on its way.";
      pdp.innerHTML = `
        <div class="pdp-gallery">
          <div class="thumbs">${thumbs.length > 1 ? thumbs.map((src, i) => `<button aria-label="View photo ${i + 1}" aria-current="${i === 0}" data-thumb="${esc(src)}"><img src="${esc(src)}" alt=""></button>`).join("") : ""}</div>
          <div class="pdp-main-img"><img id="mainImg" src="${esc(sized(thumbs[0], 900))}" alt="${esc(p.title)}"></div>
        </div>
        <div class="pdp-info">
          <div class="eyebrow">${p.own ? "B&amp;R Original" : p.isNew ? "New arrival" : esc(typeLabel(p.type))}</div>
          <h1>${esc(p.title)}</h1>
          ${p.subtitle ? `<p class="lede" style="margin-top:-8px">${esc(p.subtitle)}</p>` : ""}
          <div class="price" id="pdpPrice">${priceHtml(p)}</div>
          ${p.colors.length > 1 ? `<div class="size-row"><div class="label-line"><span>Color: <strong id="colorSel">Choose one</strong></span></div><div class="colors">${p.colors.map(c => `<button class="size-chip" data-pcolor="${esc(c)}" aria-pressed="false">${esc(niceColor(c))}</button>`).join("")}</div></div>` : p.colors.length === 1 ? `<div style="font-size:14px">Color: <strong>${esc(niceColor(p.colors[0]))}</strong></div>` : ""}
          ${!oneSize ? `<div class="size-row"><div class="label-line"><span>Size: <strong id="sizeSel">Choose one</strong></span>${desc.table ? `<button data-open-size>Size chart</button>` : ""}</div><div class="sizes">${p.sizeLabels.map(s => `<button class="size-chip" data-psize="${esc(s)}" aria-pressed="false">${esc(s)}</button>`).join("")}</div></div>` : ""}
          <div class="buy-row">
            <div class="qty"><button aria-label="Less" data-q="-1">−</button><output id="qtyOut">1</output><button aria-label="More" data-q="1">+</button></div>
            <button class="btn" id="addBtn" data-add>Add to cart</button>
          </div>
          <div class="pdp-msg" id="pdpMsg" role="status"></div>
          <div class="ship-note">${p.own ? "Printed to order" : p.ships === "usa" ? "Ships from the USA" : "Ships from overseas"}</div>
          <div class="details">
            <details open><summary>Details</summary><div class="body desc">${desc.html || "<div>Details coming soon.</div>"}</div></details>
            ${desc.table ? `<details id="sizeDetails"><summary>Size chart</summary><div class="body">${desc.table}</div></details>` : ""}
            <details><summary>Shipping</summary><div class="body"><p>${ship}</p></div></details>
            ${S.returnPolicy ? `<details><summary>Returns</summary><div class="body"><p>${esc(S.returnPolicy)}</p><p><a href="/policies/refund-policy">Read the full return policy</a></p></div></details>` : ""}
          </div>
        </div>`;
      $("related").innerHTML = related.map(card).join("");
      refresh();
    }

    pdp.addEventListener("click", async e => {
      if (!p) return;
      const t = e.target.closest("[data-thumb]");
      if (t) { $("mainImg").src = sized(t.dataset.thumb, 900); pdp.querySelectorAll("[data-thumb]").forEach(b => b.setAttribute("aria-current", b === t)); }
      const s = e.target.closest("[data-psize]");
      if (s) { st.size = s.dataset.psize; $("sizeSel").textContent = st.size + (s.classList.contains("out") ? " (sold out)" : ""); pdp.querySelectorAll("[data-psize]").forEach(b => b.setAttribute("aria-pressed", b === s)); $("pdpMsg").textContent = ""; refresh(); }
      const c = e.target.closest("[data-pcolor]");
      if (c) {
        st.color = c.dataset.pcolor;
        $("colorSel").textContent = niceColor(st.color);
        pdp.querySelectorAll("[data-pcolor]").forEach(b => b.setAttribute("aria-pressed", b === c));
        const img = p.colorImgs[st.color];
        if (img) { $("mainImg").src = sized(img, 900); pdp.querySelectorAll("[data-thumb]").forEach(b => b.setAttribute("aria-current", b.dataset.thumb === img)); }
        if (st.size && $("sizeSel")) $("sizeSel").textContent = st.size + (inStock(st.color, st.size) ? "" : " (sold out)");
        $("pdpMsg").textContent = "";
        refresh();
      }
      const q = e.target.closest("[data-q]");
      if (q) { st.qty = Math.max(1, Math.min(9, st.qty + Number(q.dataset.q))); $("qtyOut").textContent = st.qty; }
      if (e.target.closest("[data-open-size]")) { const d = $("sizeDetails"); d.open = true; d.scrollIntoView({ block: "nearest" }); }
      if (e.target.closest("[data-add]")) {
        if (p.colors.length > 1 && !st.color) { $("pdpMsg").textContent = "Please choose a color first."; return; }
        if (st.size == null) { $("pdpMsg").textContent = "Please choose a size first."; return; }
        const v = current();
        if (!v) { $("pdpMsg").textContent = "Please choose a size first."; return; }
        if (!v[3]) { $("pdpMsg").textContent = "That one's sold out. Try another size or color."; return; }
        const btn = $("addBtn");
        btn.disabled = true; btn.textContent = "Adding…";
        try { await addToCart(v[4], st.qty); $("pdpMsg").textContent = ""; }
        catch (err) { $("pdpMsg").textContent = err.message || "We couldn't add that to your cart. Please try again."; }
        refresh();
      }
    });

    loadCatalog("all").then(({ products, featured }) => {
      p = products.find(x => x.listings.includes(handle));
      if (!p) return; // keep the server-rendered product form
      render(featured.filter(x => x.id !== p.id && x.type === p.type).slice(0, 4));
    }).catch(() => {});
  }

  // ---------- cart drawer (Shopify cart API) ----------
  const drawer = $("drawer"), scrim = $("scrim");
  function openCart() { drawer.classList.add("open"); drawer.setAttribute("aria-hidden", "false"); scrim.classList.add("show"); $("drawerClose").focus(); }
  function closeAll() {
    drawer.classList.remove("open"); drawer.setAttribute("aria-hidden", "true");
    const f = $("filters"); if (f) f.classList.remove("open");
    scrim.classList.remove("show");
  }
  async function addToCart(id, quantity) {
    const r = await fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: [{ id, quantity }] }) });
    if (!r.ok) { let msg = "We couldn't add that to your cart."; try { msg = (await r.json()).description || msg; } catch (e) {} throw new Error(msg); }
    await renderCart();
    openCart();
  }
  async function renderCart() {
    const cart = await (await fetch("/cart.js", { headers: { Accept: "application/json" } })).json();
    $("cartCount").textContent = cart.item_count;
    if (!cart.items.length) {
      $("drawerItems").innerHTML = `<div class="drawer-empty"><img src="${esc(S.badge || "")}" alt="" width="1200" height="1200"><p>Your cart is empty for now.</p><a class="btn ghost" href="/collections/all?show=new">Shop New Arrivals</a></div>`;
      $("drawerFoot").innerHTML = "";
      return;
    }
    // A variant's own photo can be one she chose to hide; fall back to the product's main photo then.
    const needsSwap = cart.items.some(i => !visible(i.image));
    const catalog = needsSwap ? await loadCatalog("all").catch(() => null) : null;
    $("drawerItems").innerHTML = cart.items.map(item => {
      const own = item.vendor === OWN_VENDOR;
      const { title } = cleanTitle(item.product_title, own);
      const meta = (item.variant_title && item.variant_title !== "Default Title" ? item.variant_title.split(" / ").map(niceColor).join(" / ") + " · " : "") + "Qty " + item.quantity;
      let img = item.image ? item.image + (item.image.includes("?") ? "&" : "?") + "width=200" : "";
      if (img && !visible(img)) {
        const p = catalog && catalog.products.find(x => x.listings.includes(item.handle));
        img = p && p.imgs[0] ? sized(p.imgs[0], 200) : "";
      }
      return `<div class="line"><div class="line-img">${img ? `<img src="${esc(img)}" alt="">` : ""}</div><div><a class="line-name" href="${esc(item.url)}" style="text-decoration:none">${esc(title)}</a><div class="line-meta">${esc(meta)}</div><button class="line-remove" data-remove="${esc(item.key)}">Remove</button></div><div class="price">${money(item.final_line_price / 100)}</div></div>`;
    }).join("");
    $("drawerFoot").innerHTML = `<div class="subtotal"><span>Subtotal</span><strong>${money(cart.total_price / 100)}</strong></div><small>Shipping and taxes are calculated at checkout.</small><a class="btn block" href="/checkout">Checkout</a>`;
  }
  drawer.addEventListener("click", async e => {
    const r = e.target.closest("[data-remove]");
    if (r) {
      r.disabled = true;
      await fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ id: r.dataset.remove, quantity: 0 }) });
      renderCart();
    }
  });
  $("cartBtn").addEventListener("click", e => { if (location.pathname === "/cart") return; e.preventDefault(); renderCart(); openCart(); });
  $("drawerClose").addEventListener("click", closeAll);
  scrim.addEventListener("click", closeAll);
  document.addEventListener("keydown", e => { if (e.key === "Escape") closeAll(); });

  // ---------- mobile menu ----------
  const nav = $("nav");
  $("menuBtn").addEventListener("click", () => { const open = nav.classList.toggle("open"); $("menuBtn").setAttribute("aria-expanded", open); });

  initHome();
  initShop();
  initProduct();
})();
